import { observer } from "mobx-react-lite";
import React, { useContext } from "react";
import { Field, useForm } from "react-final-form";

import { Stack } from "@bps/fluent-ui";
import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import { ContactType } from "@libs/gateways/practice/PracticeGateway.dtos.ts";
import { capitalizeSentence } from "@libs/utils/utils.ts";
import { AddFromWaitingListButton } from "@modules/booking/screens/waiting-list/components/waiting-list-details-dialog/AddFromWaitingListButton.tsx";
import { ContactPickerFieldWithAdd } from "@modules/practice/screens/shared-components/contact-picker/ContactPickerFieldWithAdd.tsx";
import { PatientActivation } from "@modules/practice/screens/shared-components/PatientActivation.tsx";
import { AppointmentFormValues } from "@shared-types/booking/appointment-form-values.types.ts";
import { SecondColumnContent } from "@shared-types/booking/second-column-content.enum.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { FieldCondition } from "@ui-components/form/FieldCondition.tsx";
import { FieldSpy } from "@ui-components/form/FieldSpy.tsx";

import { AppointmentNoticesContactPickerWrapper } from "./appointment-notices/AppointmentNoticesContactPickerWrapper.tsx";
import { AppointmentNoticesLink } from "./appointment-notices/AppointmentNoticesLink.tsx";
import { appointmentFormNameOf } from "./AppointmentForm.types.ts";
import { AppointmentFormContext } from "./context/AppointmentFormContext.ts";

export const IndividualAppointmentFields: React.FC = observer(() => {
  const form = useForm<AppointmentFormValues>();

  const { practice, booking } = useStores();

  const {
    isGroupAppointment,
    isAppointmentValidForEdit,
    patientLabel,
    setEpisodeOfCareId
  } = useContext(AppointmentFormContext);

  if (isGroupAppointment) return null;

  return (
    <Stack>
      <AppointmentNoticesContactPickerWrapper />
      <ContactPickerFieldWithAdd
        autoFocus={!booking.ui.currentAppointment?.id}
        name={appointmentFormNameOf("patientId")}
        label={capitalizeSentence(patientLabel)}
        required
        includeInactive
        quickAddProps={{
          addButtonLabel: `New ${patientLabel}`,
          typeToAdd: ContactType.Patient,
          permission: [
            Permission.ContactRead,
            Permission.ContactWrite,
            Permission.PatientRead,
            Permission.PatientWrite
          ],
          buttonWrapperStyles: {
            root: { flexBasis: 0, flexGrow: 1.2 }
          }
        }}
        filter={{ types: [ContactType.Patient] }}
        disabled={isAppointmentValidForEdit}
        actionButton={{
          linkProps: {
            children: (
              <Field<string | undefined>
                name={appointmentFormNameOf("patientId")}
              >
                {props => {
                  if (!props.input.value) {
                    return null;
                  }
                  return <PatientActivation patientId={props.input.value} />;
                }}
              </Field>
            )
          }
        }}
        pickerFormItemStyles={{
          root: { flexBasis: 0, flexGrow: 2.8 }
        }}
      />
      <AddFromWaitingListButton linkStyles={{ root: { marginTop: 5 } }} />

      <FieldSpy
        name={appointmentFormNameOf("patientId")}
        onChange={async (value: string) => {
          //when patient name changes. episodeOfCareId has to reset
          setEpisodeOfCareId(undefined);
          form.change(appointmentFormNameOf("episodeOfCareId"), undefined);

          if (!value) return;

          const [notices] = await Promise.all([
            practice.getPatientNotices(value),
            practice.loadSystemNotices([value])
          ]);
          if (!notices) {
            return;
          }

          const adminNotices = notices.filter(
            pn => pn.patientId === value && pn.isAdmin
          );

          if (adminNotices.length > 0) {
            booking.ui.setSecondColumnContent(
              booking.ui.currentAppointment?.secondColumnContent !==
                SecondColumnContent.patientNotices
                ? SecondColumnContent.patientNotices
                : undefined
            );
          } else {
            booking.ui.setSecondColumnContent(undefined);
          }
        }}
      />

      <FieldCondition<AppointmentFormValues["patientId"]>
        when={appointmentFormNameOf("patientId")}
        is={id => !!id}
      >
        <AppointmentNoticesLink />
      </FieldCondition>
    </Stack>
  );
});
